import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { __, sprintf } from '@wordpress/i18n'
import SbUtils from '../../Utils/SbUtils'
import Checkbox from './Checkbox'
import SafeHtml from './SafeHtml'
import { TagIcon, FolderIcon, HighlightMouseCursorIcon, LinkOffIcon, CloseIcon } from './Icons/WooIcons'
import useDebounce from '../../Utils/useDebounce'

// Tab types
const TAB_PRODUCTS = 'products';
const TAB_CATEGORIES = 'categories';
const TAB_TAGS = 'tags';

/**
 * Shared WooCommerce Product Selector Component
 *
 * Used by both AddSourceModal and EditWooProductsModal for consistent UI.
 * Now supports three tabs: Products, Categories, Tags.
 *
 * @param {Object} props
 * @param {Array} props.products - All available products
 * @param {Array} props.selectedProducts - Currently selected products
 * @param {Function} props.onProductToggle - Called when a product is selected/deselected
 * @param {Function} props.onSelectAll - Called when Select All is clicked (receives filtered products)
 * @param {Function} props.onDeselectAll - Called when Deselect All is clicked
 * @param {boolean} props.isLoading - Whether products are loading
 * @param {string|null} props.validationError - Validation error message
 * @param {boolean} props.showTabs - Whether to show the tabs (default: false for backward compatibility)
 * @param {Array} props.categories - All available categories (for tabs mode)
 * @param {Array} props.selectedCategories - Currently selected categories
 * @param {Function} props.onCategoryToggle - Called when a category is selected/deselected
 * @param {boolean} props.loadingCategories - Whether categories are loading
 * @param {Array} props.tags - All available tags (for tabs mode)
 * @param {Array} props.selectedTags - Currently selected tags
 * @param {Function} props.onTagToggle - Called when a tag is selected/deselected
 * @param {boolean} props.loadingTags - Whether tags are loading
 * @param {Function} props.onClearAll - Called when Clear All is clicked (clears all selection types)
 * @param {Function} props.onSearchProducts - Called when search query changes (for server-side search)
 * @param {boolean} props.isSearching - Whether a server-side search is in progress (shows skeleton loaders)
 * @param {Function} props.onSelectAllCategories - Called when Select All categories is clicked
 * @param {Function} props.onDeselectAllCategories - Called when Deselect All categories is clicked
 * @param {Function} props.onSelectAllTags - Called when Select All tags is clicked
 * @param {Function} props.onDeselectAllTags - Called when Deselect All tags is clicked
 */
const WooProductSelector = ( props ) => {
    const {
        products = [],
        selectedProducts = [],
        onProductToggle,
        onSelectAll,
        onDeselectAll,
        isLoading = false,
        validationError = null,
        // New props for tabs mode
        showTabs = false,
        categories = [],
        selectedCategories = [],
        onCategoryToggle,
        loadingCategories = false,
        tags = [],
        selectedTags = [],
        onTagToggle,
        loadingTags = false,
        onClearAll,
        // Server-side search props
        onSearchProducts,
        isSearching = false,
        // Batch select/deselect for categories and tags
        onSelectAllCategories,
        onDeselectAllCategories,
        onSelectAllTags,
        onDeselectAllTags
    } = props;

    const [ searchQuery, setSearchQuery ] = useState( '' );
    const [ activeTab, setActiveTab ] = useState( TAB_PRODUCTS );
    // Track what query was last sent to the server (prevents flash when debounce fires)
    const [ lastSearchedQuery, setLastSearchedQuery ] = useState( '' );

    // Track if this is the initial mount (to skip empty search on mount)
    const isInitialMount = useRef( true );
    // Track the previous search query to detect actual changes
    const previousSearchQuery = useRef( '' );

    // Debounce search query for server-side search (1 second delay)
    const debouncedSearchQuery = useDebounce( searchQuery, 1000 );

    // Reset search tracking when switching tabs to prevent stale state
    useEffect( () => {
        setLastSearchedQuery( '' );
        previousSearchQuery.current = '';
    }, [ activeTab ] );

    // Detect if a search is pending or needs to be triggered
    // This prevents content flash between debounce firing and search results arriving
    const isSearchPending = useMemo( () => {
        // Only for Products tab with server-side search
        if ( activeTab !== TAB_PRODUCTS || ! onSearchProducts ) {
            return false;
        }
        // Pending if:
        // 1. User is still typing (searchQuery !== debouncedSearchQuery)
        // 2. OR debounce fired but search hasn't been triggered yet (debouncedSearchQuery !== lastSearchedQuery)
        return searchQuery !== debouncedSearchQuery || debouncedSearchQuery !== lastSearchedQuery;
    }, [ searchQuery, debouncedSearchQuery, lastSearchedQuery, activeTab, onSearchProducts ] );

    // Trigger server-side search when debounced query changes (Products tab only)
    useEffect( () => {
        // Skip initial mount - products are already loaded by parent
        if ( isInitialMount.current ) {
            isInitialMount.current = false;
            return;
        }

        // Only trigger server search for Products tab, non-URL searches, and when callback exists
        if ( ! onSearchProducts || activeTab !== TAB_PRODUCTS ) {
            return;
        }

        // Skip if query hasn't actually changed
        if ( debouncedSearchQuery === previousSearchQuery.current ) {
            return;
        }
        previousSearchQuery.current = debouncedSearchQuery;

        // Check if it looks like a URL (handled client-side)
        const query = debouncedSearchQuery.trim().toLowerCase();
        const looksLikeUrl = query.startsWith( 'http://' ) || query.startsWith( 'https://' ) ||
            query.includes( '.com' ) || query.includes( '.net' ) || query.includes( '.org' ) ||
            query.includes( '.site' ) || query.includes( 'localhost' ) || query.includes( '.local' ) ||
            query.includes( '.ddev' ) || query.includes( '/wp-admin/' );

        if ( looksLikeUrl ) {
            // Update lastSearchedQuery to resolve isSearchPending state
            // URL searches are handled client-side, but we need to mark query as "searched"
            setLastSearchedQuery( debouncedSearchQuery );
            return;
        }

        // Trigger server search for queries with 2+ characters or empty (to reset)
        if ( debouncedSearchQuery.length >= 2 || debouncedSearchQuery === '' ) {
            setLastSearchedQuery( debouncedSearchQuery );
            onSearchProducts( debouncedSearchQuery );
        } else {
            // For single-character queries, update lastSearchedQuery to resolve pending state
            // This prevents infinite loading when user types just 1 character
            setLastSearchedQuery( debouncedSearchQuery );
        }
    }, [ debouncedSearchQuery, activeTab, onSearchProducts ] );

    /**
     * Parse a product URL to extract product ID or slug
     * Handles:
     * 1. Admin edit URL: /wp-admin/post.php?post=111&action=edit
     * 2. Pretty permalink: /product/product-slug/
     * 3. Default permalink: ?p=111 or ?product_id=111
     *
     * @param {string} url - The URL to parse
     * @returns {Object|null} - { type: 'id'|'slug', value: string|number } or null
     */
    const parseProductUrl = useCallback( ( url ) => {
        if ( !url ) return null;

        const trimmedUrl = url.trim();

        try {
            // Try to parse as URL
            let urlObj;
            try {
                urlObj = new URL( trimmedUrl );
            } catch {
                // If it's not a valid URL, it might be a partial URL or just text
                return null;
            }

            const pathname = urlObj.pathname;
            const searchParams = urlObj.searchParams;

            // Case 1: Admin edit URL - /wp-admin/post.php?post=111&action=edit
            if ( pathname.includes( '/wp-admin/post.php' ) ) {
                const postId = searchParams.get( 'post' );
                if ( postId && !isNaN( parseInt( postId, 10 ) ) ) {
                    return { type: 'id', value: parseInt( postId, 10 ) };
                }
            }

            // Case 2: Default permalink - ?p=111
            const pParam = searchParams.get( 'p' );
            if ( pParam && !isNaN( parseInt( pParam, 10 ) ) ) {
                return { type: 'id', value: parseInt( pParam, 10 ) };
            }

            // Case 3: Product ID in query - ?product_id=111
            const productIdParam = searchParams.get( 'product_id' );
            if ( productIdParam && !isNaN( parseInt( productIdParam, 10 ) ) ) {
                return { type: 'id', value: parseInt( productIdParam, 10 ) };
            }

            // Case 4: Pretty permalink - /product/product-slug/ or just /product-slug/
            // Remove trailing slash and get path segments
            const pathSegments = pathname.replace( /^\/|\/$/g, '' ).split( '/' );

            if ( pathSegments.length > 0 ) {
                // Check if it's a /product/slug pattern
                const productIndex = pathSegments.indexOf( 'product' );
                if ( productIndex !== -1 && pathSegments.length > productIndex + 1 ) {
                    const slug = pathSegments[ productIndex + 1 ];
                    // Clean up slug (remove file extensions)
                    const cleanSlug = slug.replace( /\.(html|htm|php)$/, '' );
                    if ( cleanSlug ) {
                        return { type: 'slug', value: cleanSlug };
                    }
                }

                // Fallback: use the last segment as slug
                const lastSegment = pathSegments[ pathSegments.length - 1 ];
                const cleanSlug = lastSegment.replace( /\.(html|htm|php)$/, '' );
                // Only use if it's not a common path like 'shop', 'cart', 'checkout'
                const excludedPaths = [ 'shop', 'cart', 'checkout', 'my-account', 'wp-admin', 'wp-content', 'wp-includes' ];
                if ( cleanSlug && !excludedPaths.includes( cleanSlug.toLowerCase() ) ) {
                    return { type: 'slug', value: cleanSlug };
                }
            }

        } catch ( e ) {
            // URL parsing failed, return null
            return null;
        }

        return null;
    }, [] );

    // Check if search query looks like a URL
    const isUrlSearch = useMemo( () => {
        if ( !searchQuery ) return false;
        const query = searchQuery.trim().toLowerCase();
        return query.startsWith( 'http://' ) || query.startsWith( 'https://' ) || query.includes( '.com' ) || query.includes( '.net' ) || query.includes( '.org' ) || query.includes( '.site' ) || query.includes( 'localhost' ) || query.includes( '.local' ) || query.includes( '.ddev' ) || query.includes( '/wp-admin/' );
    }, [ searchQuery ] );

    // Parse URL search to get product identifier
    const urlSearchResult = useMemo( () => {
        if ( !isUrlSearch ) return null;
        return parseProductUrl( searchQuery );
    }, [ isUrlSearch, searchQuery, parseProductUrl ] );

    // Memoize selected product IDs for faster lookup
    const selectedProductIds = useMemo( () => {
        return new Set( selectedProducts.map( p => p.id ) );
    }, [ selectedProducts ] );

    const selectedCategoryIds = useMemo( () => {
        return new Set( selectedCategories.map( c => c.id ) );
    }, [ selectedCategories ] );

    const selectedTagIds = useMemo( () => {
        return new Set( selectedTags.map( t => t.id ) );
    }, [ selectedTags ] );

    const isProductSelected = useCallback( ( productId ) => {
        return selectedProductIds.has( productId );
    }, [ selectedProductIds ] );

    const isCategorySelected = useCallback( ( categoryId ) => {
        return selectedCategoryIds.has( categoryId );
    }, [ selectedCategoryIds ] );

    const isTagSelected = useCallback( ( tagId ) => {
        return selectedTagIds.has( tagId );
    }, [ selectedTagIds ] );

    const filteredProducts = useMemo( () => {
        // URL search is handled client-side (filter by parsed ID or slug)
        if ( isUrlSearch && urlSearchResult ) {
            return products.filter( product => {
                if ( urlSearchResult.type === 'id' ) {
                    return product.id === urlSearchResult.value;
                } else if ( urlSearchResult.type === 'slug' ) {
                    // Match by exact slug or partial slug match
                    return product.slug === urlSearchResult.value ||
                           ( product.slug && product.slug.toLowerCase() === urlSearchResult.value.toLowerCase() );
                }
                return false;
            });
        }

        // If it looks like a URL but couldn't be parsed, return empty
        // This shows the "no products found with that URL" message
        if ( isUrlSearch ) {
            return [];
        }

        // For text search, server-side search handles filtering
        // Just return the products from props (already filtered by server)
        return products;
    }, [ products, isUrlSearch, urlSearchResult ] );

    const filteredCategories = useMemo( () => {
        return categories.filter( category => {
            if ( !searchQuery ) return true;
            const query = searchQuery.toLowerCase();
            return category.name.toLowerCase().includes( query );
        });
    }, [ categories, searchQuery ] );

    const filteredTags = useMemo( () => {
        return tags.filter( tag => {
            if ( !searchQuery ) return true;
            const query = searchQuery.toLowerCase();
            return tag.name.toLowerCase().includes( query );
        });
    }, [ tags, searchQuery ] );

    // Check if all filtered products are selected
    const allFilteredProductsSelected = useMemo( () => {
        return filteredProducts.length > 0 && filteredProducts.every( p => selectedProductIds.has( p.id ) );
    }, [ filteredProducts, selectedProductIds ] );

    // Check if all filtered categories are selected
    const allFilteredCategoriesSelected = useMemo( () => {
        return filteredCategories.length > 0 && filteredCategories.every( c => selectedCategoryIds.has( c.id ) );
    }, [ filteredCategories, selectedCategoryIds ] );

    // Check if all filtered tags are selected
    const allFilteredTagsSelected = useMemo( () => {
        return filteredTags.length > 0 && filteredTags.every( t => selectedTagIds.has( t.id ) );
    }, [ filteredTags, selectedTagIds ] );

    // Toggle select all / deselect all products
    const handleToggleAllProducts = useCallback( () => {
        if ( allFilteredProductsSelected ) {
            // Deselect all filtered products (pass filtered items for selective deselection)
            if ( onDeselectAll ) {
                onDeselectAll( filteredProducts );
            }
        } else {
            // Select all filtered products (merge with existing selection)
            if ( onSelectAll ) {
                onSelectAll( filteredProducts );
            }
        }
    }, [ allFilteredProductsSelected, filteredProducts, onSelectAll, onDeselectAll ] );

    // Toggle select all / deselect all categories
    const handleToggleAllCategories = useCallback( () => {
        if ( allFilteredCategoriesSelected ) {
            // Deselect all filtered categories (pass filtered items for selective deselection)
            if ( onDeselectAllCategories ) {
                onDeselectAllCategories( filteredCategories );
            }
        } else {
            // Select all filtered categories (merge with existing selection)
            if ( onSelectAllCategories ) {
                onSelectAllCategories( filteredCategories );
            }
        }
    }, [ allFilteredCategoriesSelected, filteredCategories, onSelectAllCategories, onDeselectAllCategories ] );

    // Toggle select all / deselect all tags
    const handleToggleAllTags = useCallback( () => {
        if ( allFilteredTagsSelected ) {
            // Deselect all filtered tags (pass filtered items for selective deselection)
            if ( onDeselectAllTags ) {
                onDeselectAllTags( filteredTags );
            }
        } else {
            // Select all filtered tags (merge with existing selection)
            if ( onSelectAllTags ) {
                onSelectAllTags( filteredTags );
            }
        }
    }, [ allFilteredTagsSelected, filteredTags, onSelectAllTags, onDeselectAllTags ] );

    // Get total selection count
    const totalSelectionCount = selectedProducts.length + selectedCategories.length + selectedTags.length;

    // Get search placeholder based on active tab
    const getSearchPlaceholder = () => {
        switch ( activeTab ) {
            case TAB_CATEGORIES:
                return __( 'Search Categories...', 'sb-customizer' );
            case TAB_TAGS:
                return __( 'Search Tags...', 'sb-customizer' );
            default:
                return __( 'Search a Product or paste a product URL', 'sb-customizer' );
        }
    };

    // Render tab buttons
    const renderTabs = () => {
        if ( !showTabs ) return null;

        return (
            <div className='sb-woo-selector-tabs'>
                <button
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_PRODUCTS ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_PRODUCTS ); setSearchQuery( '' ); } }
                >
                    { __( 'Products', 'sb-customizer' ) }
                </button>
                <button
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_CATEGORIES ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_CATEGORIES ); setSearchQuery( '' ); } }
                >
                    { __( 'Categories', 'sb-customizer' ) }
                </button>
                <button
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_TAGS ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_TAGS ); setSearchQuery( '' ); } }
                >
                    { __( 'Tags', 'sb-customizer' ) }
                </button>
            </div>
        );
    };

    // Render category item
    const renderCategoryItem = ( category ) => (
        <div
            key={ `cat-${ category.id }` }
            className={ 'sb-woo-selector-item sb-woo-selector-category-item' + ( isCategorySelected( category.id ) ? ' sb-selected' : '' ) }
            onClick={ () => onCategoryToggle && onCategoryToggle( category ) }
        >
            <div
                className='sb-woo-selector-item-checkbox'
                onClick={ ( e ) => e.stopPropagation() }
            >
                <Checkbox
                    value={ isCategorySelected( category.id ) }
                    enabled={ true }
                    onChange={ () => onCategoryToggle && onCategoryToggle( category ) }
                />
            </div>
            <div className='sb-woo-selector-item-icon'>
                <FolderIcon />
            </div>
            <div className='sb-woo-selector-item-info'>
                <span className='sb-woo-selector-item-name'>{ category.name }</span>
                <span className='sb-woo-selector-item-meta'>
                    { category.product_count } { category.product_count === 1 ? __( 'Product', 'sb-customizer' ) : __( 'Products', 'sb-customizer' ) }
                    { ' • ' }
                    { category.review_count } { category.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                </span>
            </div>
        </div>
    );

    // Render tag item
    const renderTagItem = ( tag ) => (
        <div
            key={ `tag-${ tag.id }` }
            className={ 'sb-woo-selector-item sb-woo-selector-tag-item' + ( isTagSelected( tag.id ) ? ' sb-selected' : '' ) }
            onClick={ () => onTagToggle && onTagToggle( tag ) }
        >
            <div
                className='sb-woo-selector-item-checkbox'
                onClick={ ( e ) => e.stopPropagation() }
            >
                <Checkbox
                    value={ isTagSelected( tag.id ) }
                    enabled={ true }
                    onChange={ () => onTagToggle && onTagToggle( tag ) }
                />
            </div>
            <div className='sb-woo-selector-item-icon'>
                <TagIcon />
            </div>
            <div className='sb-woo-selector-item-info'>
                <span className='sb-woo-selector-item-name'>{ tag.name }</span>
                <span className='sb-woo-selector-item-meta'>
                    { tag.product_count } { tag.product_count === 1 ? __( 'Product', 'sb-customizer' ) : __( 'Products', 'sb-customizer' ) }
                    { ' • ' }
                    { tag.review_count } { tag.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                </span>
            </div>
        </div>
    );

    // Render skeleton loaders for products loading state
    const renderSkeletonLoaders = useCallback( ( count = 6 ) => {
        return Array.from( { length: count } ).map( ( _, index ) => (
            <div key={ `skeleton-${ index }` } className='sb-woo-selector-skeleton'>
                <div className='sb-woo-selector-skeleton-checkbox'></div>
                <div className='sb-woo-selector-skeleton-image'></div>
                <div className='sb-woo-selector-skeleton-info'>
                    <div className='sb-woo-selector-skeleton-name'></div>
                    <div className='sb-woo-selector-skeleton-meta'></div>
                </div>
                <div className='sb-woo-selector-skeleton-price'></div>
            </div>
        ) );
    }, [] );

    // Render skeleton loaders for categories/tags loading state
    const renderCategoryTagSkeletonLoaders = useCallback( ( count = 6 ) => {
        return Array.from( { length: count } ).map( ( _, index ) => (
            <div key={ `skeleton-${ index }` } className='sb-woo-selector-skeleton sb-woo-selector-skeleton-category'>
                <div className='sb-woo-selector-skeleton-checkbox'></div>
                <div className='sb-woo-selector-skeleton-icon'></div>
                <div className='sb-woo-selector-skeleton-info'>
                    <div className='sb-woo-selector-skeleton-name'></div>
                    <div className='sb-woo-selector-skeleton-meta'></div>
                </div>
            </div>
        ) );
    }, [] );

    // Render list content based on active tab
    const renderListContent = () => {
        // Products tab
        if ( activeTab === TAB_PRODUCTS ) {
            // Show skeleton loaders for initial load, active search, or pending search
            // isSearchPending now covers: typing, clearing, AND debounce fired but search not yet triggered
            if ( isSearching || isLoading || isSearchPending ) {
                return renderSkeletonLoaders( 6 );
            }

            if ( filteredProducts.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { isUrlSearch ? <LinkOffIcon /> : SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>
                                { isUrlSearch
                                    ? __( 'No products found with that URL', 'sb-customizer' )
                                    : __( 'No results found', 'sb-customizer' )
                                }
                            </span>
                            <span className='sb-woo-selector-empty-message'>
                                { isUrlSearch
                                    ? __( 'We couldn\'t find any product related to that url. Please double check it and try again.', 'sb-customizer' )
                                    : sprintf( __( 'We couldn\'t find any results for "%s". Please try a different term.', 'sb-customizer' ), searchQuery )
                                }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredProducts.map( ( product ) => (
                <div
                    key={ product.id }
                    className={ 'sb-woo-selector-item' + ( isProductSelected( product.id ) ? ' sb-selected' : '' ) }
                    onClick={ () => onProductToggle( product ) }
                >
                    <div
                        className='sb-woo-selector-item-checkbox'
                        onClick={ ( e ) => e.stopPropagation() }
                    >
                        <Checkbox
                            value={ isProductSelected( product.id ) }
                            enabled={ true }
                            onChange={ () => onProductToggle( product ) }
                        />
                    </div>
                    <div className='sb-woo-selector-item-image'>
                        {
                            product.image
                                ? <img src={ product.image } alt={ product.name } />
                                : <div className='sb-woo-selector-item-placeholder'></div>
                        }
                    </div>
                    <div className='sb-woo-selector-item-info'>
                        <span className='sb-woo-selector-item-name'>{ product.name }</span>
                        <span className='sb-woo-selector-item-meta'>
                            { product.review_count } { product.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                            { product.sku && <> • SKU { product.sku }</> }
                        </span>
                    </div>
                    <SafeHtml
                        className='sb-woo-selector-item-price'
                        html={ product.price_html }
                        as='div'
                    />
                </div>
            ));
        }

        // Categories tab
        if ( activeTab === TAB_CATEGORIES ) {
            if ( loadingCategories ) {
                return renderCategoryTagSkeletonLoaders( 6 );
            }

            if ( filteredCategories.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No categories found', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { sprintf( __( 'We couldn\'t find any categories for "%s". Please try a different term.', 'sb-customizer' ), searchQuery ) }
                            </span>
                        </div>
                    </div>
                );
            }

            if ( filteredCategories.length === 0 ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        <FolderIcon />
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No categories available', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { __( 'No product categories found in your store.', 'sb-customizer' ) }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredCategories.map( renderCategoryItem );
        }

        // Tags tab
        if ( activeTab === TAB_TAGS ) {
            if ( loadingTags ) {
                return renderCategoryTagSkeletonLoaders( 6 );
            }

            if ( filteredTags.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No tags found', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { sprintf( __( 'We couldn\'t find any tags for "%s". Please try a different term.', 'sb-customizer' ), searchQuery ) }
                            </span>
                        </div>
                    </div>
                );
            }

            if ( filteredTags.length === 0 ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        <TagIcon />
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No tags available', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { __( 'No product tags found in your store.', 'sb-customizer' ) }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredTags.map( renderTagItem );
        }

        return null;
    };

    // Render selected items in selection panel
    const renderSelectionItems = () => {
        if ( !showTabs ) {
            // Legacy mode - only products
            return selectedProducts.map( ( product ) => (
                <div key={ product.id } className='sb-woo-selector-selected-item'>
                    <span className='sb-woo-selector-selected-name'>{ product.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onProductToggle( product );
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            ));
        }

        // Tabs mode - show mixed items with icons
        const items = [];

        // Add tags first
        selectedTags.forEach( tag => {
            items.push(
                <div key={ `tag-${ tag.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-tag'>
                        <TagIcon />
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ tag.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onTagToggle && onTagToggle( tag );
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        // Add categories
        selectedCategories.forEach( category => {
            items.push(
                <div key={ `cat-${ category.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-category'>
                        <FolderIcon />
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ category.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onCategoryToggle && onCategoryToggle( category );
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        // Add products
        selectedProducts.forEach( product => {
            items.push(
                <div key={ `prod-${ product.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-product'>
                        { product.image
                            ? <img src={ product.image } alt={ product.name } />
                            : <div className='sb-woo-selector-item-placeholder'></div>
                        }
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ product.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onProductToggle( product );
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        return items;
    };

    // Handle clear all
    const handleClearAll = () => {
        if ( showTabs && onClearAll ) {
            onClearAll();
        } else if ( onDeselectAll ) {
            onDeselectAll();
        }
    };

    // Determine which header/footer to show based on active tab
    const showProductHeader = activeTab === TAB_PRODUCTS;
    const showCategoryHeader = activeTab === TAB_CATEGORIES;
    const showTagHeader = activeTab === TAB_TAGS;

    return (
        <div className='sb-woo-selector-container'>
            {/* Two-panel layout */}
            <div className='sb-woo-selector-layout'>
                {/* Left Panel - Item List */}
                <div className='sb-woo-selector-products'>
                    {/* Tabs */}
                    { renderTabs() }

                    {/* Search Input */}
                    <div className='sb-woo-selector-search'>
                        <div className='sb-woo-selector-search-icon'>
                            { SbUtils.printIcon( 'search', false, false, 16 ) }
                        </div>
                        <input
                            type='text'
                            className={ 'sb-woo-selector-search-input' }
                            placeholder={ getSearchPlaceholder() }
                            value={ searchQuery }
                            onChange={ ( e ) => setSearchQuery( e.target.value ) }
                        />
                    </div>

                    {/* Item List Container */}
                    <div className='sb-woo-selector-list-container'>
                        {/* Header Row - Products Tab */}
                        { showProductHeader && (
                            <div className='sb-woo-selector-list-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredProducts.length > 0 ? allFilteredProductsSelected : true }
                                        enabled={ filteredProducts.length > 0 }
                                        onChange={ handleToggleAllProducts }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Product', 'sb-customizer' ) }</span>
                                <span className='sb-woo-selector-header-price'>{ __( 'Price', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Header Row - Categories Tab */}
                        { showCategoryHeader && (
                            <div className='sb-woo-selector-category-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredCategories.length > 0 ? allFilteredCategoriesSelected : true }
                                        enabled={ filteredCategories.length > 0 }
                                        onChange={ handleToggleAllCategories }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Categories', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Header Row - Tags Tab */}
                        { showTagHeader && (
                            <div className='sb-woo-selector-tag-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredTags.length > 0 ? allFilteredTagsSelected : true }
                                        enabled={ filteredTags.length > 0 }
                                        onChange={ handleToggleAllTags }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Tags', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Scrollable Item List */}
                        <div
                            className={ 'sb-woo-selector-list' + ( showTabs ? ' sb-with-tabs' : '' ) }
                        >
                            { renderListContent() }
                        </div>

                        {/* Footer Row - Products Tab */}
                        { showProductHeader && (
                            <div className='sb-woo-selector-list-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredProducts.length > 0 ? allFilteredProductsSelected : true }
                                        enabled={ filteredProducts.length > 0 }
                                        onChange={ handleToggleAllProducts }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Product', 'sb-customizer' ) }</span>
                                <span className='sb-woo-selector-header-price'>{ __( 'Price', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Footer Row - Categories Tab */}
                        { showCategoryHeader && (
                            <div className='sb-woo-selector-category-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredCategories.length > 0 ? allFilteredCategoriesSelected : true }
                                        enabled={ filteredCategories.length > 0 }
                                        onChange={ handleToggleAllCategories }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Categories', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Footer Row - Tags Tab */}
                        { showTagHeader && (
                            <div className='sb-woo-selector-tag-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredTags.length > 0 ? allFilteredTagsSelected : true }
                                        enabled={ filteredTags.length > 0 }
                                        onChange={ handleToggleAllTags }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Tags', 'sb-customizer' ) }</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Selected Items */}
                <div className='sb-woo-selector-selected'>
                    {/* Selected Items Box */}
                    <div className={ 'sb-woo-selector-selected-box' + ( ( showTabs ? totalSelectionCount : selectedProducts.length ) > 0 ? ' sb-has-items' : '' ) }>
                        {/* Header */}
                        <div className='sb-woo-selector-selected-header'>
                            <span className='sb-text-small sb-bold'>
                                { __( 'Your Selection', 'sb-customizer' ) }
                            </span>
                            {
                                ( showTabs ? totalSelectionCount : selectedProducts.length ) > 0 &&
                                <button
                                    className='sb-woo-selector-clear-all'
                                    onClick={ handleClearAll }
                                >
                                    { __( 'Clear All', 'sb-customizer' ) }
                                </button>
                            }
                        </div>

                        {/* Validation Error Banner - Inline below header */}
                        {
                            validationError &&
                            <div className='sb-woo-selector-error-banner'>
                                { SbUtils.printIcon( 'warning', false, false, 16 ) }
                                <span className='sb-woo-selector-error-text'>{ validationError }</span>
                            </div>
                        }

                        {/* Selected List */}
                        <div className='sb-woo-selector-selected-list'>
                            {
                                ( showTabs ? totalSelectionCount : selectedProducts.length ) === 0 &&
                                <div className='sb-woo-selector-selected-empty'>
                                    <HighlightMouseCursorIcon />
                                    <div className='sb-woo-selector-empty-text'>
                                        <span className='sb-woo-selector-empty-title'>
                                            { __( 'No products selected', 'sb-customizer' ) }
                                        </span>
                                        <span className='sb-woo-selector-empty-desc'>
                                            { __( 'As you select products, categories or tags, they will show up here', 'sb-customizer' ) }
                                        </span>
                                    </div>
                                </div>
                            }

                            { renderSelectionItems() }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WooProductSelector;
